import { StandardEvents } from '@shopify/events';

const instances = new Set();
let inputWatch;

class UMInstallmentButton extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.generation = (this.generation || 0) + 1;
    this.pending = false;
    this.busy = false;
    this.invalidSelection = false;
    this.term = 4;
    this.scope = this.closest('product-card') || this.closest('.rp-fast') || this.closest('dialog') || this.closest('.shopify-section') || this.parentElement;
    this.button = this.querySelector('[data-um-installment-purchase]');
    this.price = this.querySelector('[data-um-installment-price]');
    this.label = this.querySelector('[data-um-installment-label]');
    this.status = this.querySelector('[data-um-installment-status]');
    this.dialog = this.querySelector('[data-um-rate-dialog]');
    this.consent = this.querySelector('[data-um-rate-consent]');
    this.continueButton = this.querySelector('[data-um-rate-continue]');
    this.dialogError = this.querySelector('[data-um-rate-error]');
    this.termInputs = Array.from(this.querySelectorAll('[data-um-rate-term]'));
    if (!this.button || !this.scope || !this.readData()) return;
    const { signal } = this.abort;
    this.watchStickyDetails(signal);
    this.button.addEventListener('click', event => this.openDialog(event), { signal });
    this.querySelector('[data-um-rate-close]')?.addEventListener('click', () => this.closeDialog(), { signal });
    this.continueButton?.addEventListener('click', event => this.purchase(event), { signal });
    this.consent?.addEventListener('change', () => this.refreshDialog(), { signal });
    for (const input of this.termInputs) input.addEventListener('change', () => {
      if (!input.checked) return;
      this.term = Number(input.value);
      this.consent.checked = false;
      this.dialogSelection = this.selectionKey();
      this.setDialogError('');
      this.refresh();
    }, { signal });
    this.dialog?.addEventListener('close', () => this.finishDialog(), { signal });
    this.dialog?.addEventListener('cancel', event => { event.preventDefault(); this.closeDialog(); }, { signal });
    this.dialog?.addEventListener('click', event => {
      if (event.target !== this.dialog) return;
      const bounds = this.dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.closeDialog();
    }, { signal });
    this.scope.addEventListener('um:variant-changed', event => this.variantChanged(event), { signal });
    this.scope.addEventListener('um:sync-variant', event => this.variantChanged(event), { signal });
    this.scope.addEventListener(StandardEvents.productSelect, event => this.productSelected(event), { signal });
    this.scope.addEventListener('change', () => this.scheduleRefresh(), { signal });
    this.scope.addEventListener('input', () => this.scheduleRefresh(), { signal });
    window.addEventListener('pageshow', () => {
      this.closeDialog(false);
      this.busy = false;
      if (this.scope.dataset.umInstallmentPending === 'true') {
        delete this.scope.dataset.umInstallmentPending;
        delete this.scope.dataset.umPurchasePending;
        this.scope.umPurchaseCoordinator?.refreshControls();
      }
      this.refresh();
    }, { signal });
    this.observer = new MutationObserver(() => this.scheduleRefresh());
    this.observer.observe(this.scope, { subtree: true, childList: true, attributes: true, attributeFilter: ['value', 'disabled', 'data-um-purchase-pending'] });
    instances.add(this);
    if (!inputWatch) inputWatch = window.setInterval(() => instances.forEach(instance => instance.refresh()), 250);
    this.refresh();
  }

  disconnectedCallback() {
    this.closeDialog(false);
    this.abort?.abort();
    this.observer?.disconnect();
    this.stickyObserver?.disconnect();
    cancelAnimationFrame(this.stickyFrame);
    this.stickyFrame = undefined;
    cancelAnimationFrame(this.frame);
    this.frame = undefined;
    instances.delete(this);
    if (this.stickyDetails && !Array.from(instances).some(instance => instance.stickyDetails === this.stickyDetails)) {
      this.stickyDetails.classList.remove('um-installments-tall-details');
    }
    if (!instances.size && inputWatch) {
      window.clearInterval(inputWatch);
      inputWatch = undefined;
    }
  }

  watchStickyDetails(signal) {
    this.stickyDetails = this.closest('.product-details.sticky-content--desktop');
    if (!this.stickyDetails) return;
    this.stickyContent = this.stickyDetails.classList.contains('full-height--desktop')
      ? this.stickyDetails.querySelector(':scope > .group-block') || this.stickyDetails
      : this.stickyDetails;
    this.stickyObserver = new ResizeObserver(() => this.scheduleStickyCheck());
    this.stickyObserver.observe(this.stickyDetails);
    if (this.stickyContent !== this.stickyDetails) this.stickyObserver.observe(this.stickyContent);
    window.addEventListener('resize', () => this.scheduleStickyCheck(), { signal, passive: true });
    window.addEventListener('scroll', () => this.scheduleStickyCheck(), { signal, passive: true });
    this.scheduleStickyCheck();
  }

  scheduleStickyCheck() {
    if (this.stickyFrame) return;
    this.stickyFrame = requestAnimationFrame(() => {
      this.stickyFrame = undefined;
      this.updateStickyDetails();
    });
  }

  updateStickyDetails() {
    if (!this.isConnected || !this.stickyDetails || !this.stickyContent) return;
    const offset = Math.max(0, Number.parseFloat(getComputedStyle(this.stickyDetails).getPropertyValue('--sticky-header-offset')) || 0);
    const tooTall = window.matchMedia('(min-width: 750px)').matches
      && this.stickyContent.getBoundingClientRect().height > window.innerHeight - offset;
    this.stickyDetails.classList.toggle('um-installments-tall-details', tooTall);
  }

  readData() {
    const text = this.querySelector('[data-um-installment-data]')?.textContent;
    if (!text) return false;
    if (text === this.dataText) return true;
    try {
      const data = JSON.parse(text);
      if (!Array.isArray(data.variants)) return false;
      this.data = data;
      this.dataText = text;
      this.variants = new Map(data.variants.map(variant => [String(variant.id), variant]));
      this.originals = new Map();
      this.installments = new Map([[3, new Map()], [4, new Map()]]);
      data.variants.forEach(variant => {
        const type = variant.options[data.paymentIndex];
        const key = this.optionKey(variant);
        if (data.paymentIndex < 0 || type === 'Intero') this.originals.set(key, variant);
        const term = type === '3 rate' ? 3 : type === '4 rate' ? 4 : 0;
        if (data.paymentIndex >= 0 && term) this.installments.get(term).set(key, variant);
      });
      return true;
    } catch (_) {
      return false;
    }
  }

  optionKey(variant) {
    return JSON.stringify(variant.options.filter((_, index) => index !== this.data.paymentIndex));
  }

  form() {
    const ownForm = this.closest('form[action*="/cart/add"]');
    if (ownForm) return ownForm;
    if (this.dataset.formId) {
      const linked = document.getElementById(this.dataset.formId);
      if (linked instanceof HTMLFormElement && this.scope.contains(linked)) return linked;
    }
    const components = this.scope.querySelectorAll('product-form-component');
    for (const component of components) {
      if (String(component.dataset.productId) !== String(this.data.productId)) continue;
      const form = component.querySelector('form[action*="/cart/add"]');
      if (form) return form;
    }
    return null;
  }

  selectedOriginal() {
    const form = this.form();
    const field = form?.querySelector('[name="id"]');
    const id = field ? field.value : this.selectedId || this.data.initialId;
    const selected = this.variants.get(String(id));
    if (!selected) return null;
    return this.originals.get(this.optionKey(selected)) || null;
  }

  quantity(original) {
    const form = this.form();
    const field = form?.querySelector('[name="quantity"]');
    const defaultQuantity = form?.closest('product-form-component')?.dataset.quantityDefault;
    return Number(field ? field.value : defaultQuantity || original?.quantityRule?.min || 1);
  }

  validQuantity(quantity, variant) {
    if (!Number.isSafeInteger(quantity) || quantity < 1) return false;
    const rule = variant?.quantityRule || {};
    const min = Number(rule.min) || 1;
    const increment = Number(rule.increment) || 1;
    return quantity >= min && (variant?.stockMax == null || quantity <= Number(variant.stockMax)) && (rule.max == null || quantity <= Number(rule.max)) && (quantity - min) % increment === 0;
  }

  variantChanged(event) {
    if (!this.isOwnEvent(event)) return;
    const id = event.detail?.id;
    if (id == null) return;
    this.selectedId = String(id);
    this.invalidSelection = !this.variants.has(this.selectedId);
    this.scheduleRefresh();
  }

  isOwnEvent(event) {
    if (!(event.target instanceof Element)) return false;
    const card = event.target.closest('product-card');
    if (card && card !== this.scope) return false;
    const productId = event.product?.id || event.target.closest('[data-product-id]')?.dataset.productId;
    return !productId || String(productId) === String(this.data.productId);
  }

  productSelected(event) {
    if (!this.isOwnEvent(event)) return;
    const generation = ++this.generation;
    this.pending = true;
    this.refresh();
    Promise.resolve(event.promise).then(result => {
      if (generation !== this.generation || !this.isConnected) return;
      const productId = result?.detail?.productId;
      const variant = result?.detail?.resource || result?.variant;
      if (productId && String(productId) !== String(this.data.productId)) {
        this.invalidSelection = true;
        return;
      }
      this.readData();
      const id = variant?.id;
      this.invalidSelection = id == null || variant?.available === false || !this.variants.has(String(id));
      if (id != null) this.selectedId = String(id);
    }).catch(error => {
      if (generation !== this.generation || !this.isConnected) return;
      this.invalidSelection = true;
      if (error?.name !== 'AbortError') this.showStatus(this.data.labels.selectionFailed, true);
    }).finally(() => {
      if (generation !== this.generation || !this.isConnected) return;
      this.pending = false;
      this.refresh();
    });
  }

  scheduleRefresh() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = undefined;
      this.refresh();
    });
  }

  refresh() {
    if (!this.isConnected || !this.button || !this.readData()) return;
    const currentInput = this.form()?.querySelector('[name="id"]');
    const currentId = currentInput?.value;
    if (this.lastInputId !== undefined && currentId !== this.lastInputId && !this.pending) {
      this.invalidSelection = !this.variants.has(String(currentId));
    }
    this.lastInputId = currentId;
    const original = this.selectedOriginal();
    const quarter = original && this.installmentFor(original);
    const quantity = this.quantity(original);
    this.hidden = !original || original.price <= 5000;
    const coordinator = this.scope.umPurchaseCoordinator;
    const locked = this.busy || this.pending || coordinator?.pending || coordinator?.invalid || coordinator?.busy || this.scope.dataset.umPurchasePending === 'true';
    const available = Boolean(original?.available && quarter?.available && !this.invalidSelection && this.validPlan(original, quarter));
    const quantityValid = this.validQuantity(quantity, original) && this.validQuantity(quantity, quarter);
    const disabled = !available || !quantityValid || !this.form() || locked;
    if (this.button.disabled !== disabled) this.button.disabled = disabled;
    const price = quarter?.money || original?.quarterMoney;
    if (this.price && price && this.price.textContent !== price) this.price.textContent = price;
    const label = this.busy ? this.data.labels.preparing : this.data.labels.pay;
    if (this.label && this.label.textContent !== label) this.label.textContent = label;
    if (this.busy && !this.button.hasAttribute('aria-busy')) this.button.setAttribute('aria-busy', 'true');
    if (!this.busy && this.button.hasAttribute('aria-busy')) this.button.removeAttribute('aria-busy');
    if (!this.pending && !this.busy) {
      if (!available) this.showStatus(this.data.labels.unavailable);
      else if (!quantityValid) this.showStatus(this.data.labels.invalidQuantity);
      else this.showStatus('');
    }
    if (this.dialog?.open) this.refreshDialog();
  }

  showStatus(message, error = false) {
    if (!this.status) return;
    if (this.status.textContent !== message) this.status.textContent = message;
    if (this.status.hidden !== !message) this.status.hidden = !message;
    if (error) this.status.setAttribute('data-error', '');
    else this.status.removeAttribute('data-error');
  }

  installmentFor(original, term = this.term) {
    return this.installments.get(term)?.get(this.optionKey(original));
  }

  validPlan(original, quarter, term = this.term) {
    return Number.isSafeInteger(original?.price) && Number.isSafeInteger(quarter?.price)
      && original.price > 5000 && quarter.price > 0 && [3, 4].includes(term)
      && Math.abs(original.price - quarter.price * term) <= Math.floor(term / 2);
  }

  selectionKey() {
    const original = this.selectedOriginal();
    const quarter = original && this.installmentFor(original);
    return original && quarter ? [original.id, quarter.id, this.term, original.price, quarter.price, this.quantity(original)].join(':') : '';
  }

  money(cents) {
    return new Intl.NumberFormat(this.data.locale || 'it', { style: 'currency', currency: this.data.currency || 'EUR' }).format(cents / 100);
  }

  setText(selector, text) {
    const element = this.dialog?.querySelector(selector);
    if (element && element.textContent !== text) element.textContent = text;
  }

  openDialog(event) {
    event.preventDefault();
    this.term = 4;
    this.refresh();
    if (this.button.disabled || !this.dialog || this.dialog.open || !this.form()?.reportValidity()) return;
    this.consent.checked = false;
    this.dialogSelection = this.selectionKey();
    this.setDialogError('');
    this.previousOverflow = document.documentElement.style.overflow;
    this.restoreFocus = true;
    document.documentElement.style.overflow = 'hidden';
    this.dialog.showModal();
    this.refreshDialog();
  }

  closeDialog(restoreFocus = true) {
    this.restoreFocus = restoreFocus;
    if (this.dialog?.open) this.dialog.close();
    this.finishDialog();
  }

  finishDialog() {
    if (this.previousOverflow !== undefined) {
      document.documentElement.style.overflow = this.previousOverflow;
      this.previousOverflow = undefined;
    }
    if (this.consent) this.consent.checked = false;
    if (this.restoreFocus && this.button?.isConnected) this.button.focus({ preventScroll: true });
    this.restoreFocus = false;
    this.dialogSelection = '';
  }

  setDialogError(message) {
    if (!this.dialogError) return;
    if (this.dialogError.textContent !== message) this.dialogError.textContent = message;
    if (this.dialogError.hidden !== !message) this.dialogError.hidden = !message;
  }

  refreshDialog() {
    if (!this.dialog?.open) return;
    const original = this.selectedOriginal();
    const quarter = original && this.installmentFor(original);
    const quantity = this.quantity(original);
    const key = this.selectionKey();
    if (key !== this.dialogSelection) {
      this.dialogSelection = key;
      this.consent.checked = false;
      this.setDialogError(this.data.labels.changed);
    }
    const invalid = !original?.available || !quarter?.available || this.invalidSelection || !this.validPlan(original, quarter)
      || !this.validQuantity(quantity, original) || !this.validQuantity(quantity, quarter);
    const disabled = invalid || this.button.disabled || !this.consent.checked;
    if (this.continueButton.disabled !== disabled) this.continueButton.disabled = disabled;
    const label = this.busy ? this.data.labels.preparing : this.data.labels.continue;
    if (this.continueButton.textContent !== label) this.continueButton.textContent = label;
    if (this.busy) this.continueButton.setAttribute('aria-busy', 'true');
    else this.continueButton.removeAttribute('aria-busy');
    if (invalid) { this.setDialogError(this.data.labels.unavailable); return; }
    const total = original.price * quantity;
    const first = quarter.price * quantity;
    const last = total - first * (this.term - 1);
    const options = original.options.filter((_, index) => index !== this.data.paymentIndex).filter(option => option !== 'Default Title').join(' / ');
    this.setText('[data-um-rate-product]', this.data.productTitle || '');
    this.setText('[data-um-rate-variant]', [options, this.data.labels.quantity.replace('[quantity]', String(quantity))].filter(Boolean).join(' · '));
    this.setText('[data-um-rate-total]', this.money(total));
    this.setText('[data-um-rate-today]', this.money(first));
    this.setText('[data-um-rate-remaining]', this.money(total - first));
    const planText = text => String(text || '').replaceAll('[term]', String(this.term)).replaceAll('[remaining]', String(this.term - 1));
    this.setText('[data-um-rate-remaining-label]', planText(this.data.labels.remaining));
    this.setText('[data-um-rate-consent-text]', planText(this.data.labels.consent));
    this.setText('[data-um-rate-notice]', planText(this.data.labels.notice));
    for (const input of this.termInputs) {
      const term = Number(input.value);
      const candidate = this.installmentFor(original, term);
      const disabled = !candidate?.available || !this.validPlan(original, candidate, term) || !this.validQuantity(quantity, candidate) || this.busy || this.pending;
      if (input.disabled !== disabled) input.disabled = disabled;
      input.checked = term === this.term;
    }
    const schedule = last === first ? this.data.labels.equalSchedule : this.data.labels.adjustedSchedule;
    this.setText('[data-um-rate-schedule]', planText(schedule).replace('[regular]', this.money(first)).replace('[last]', this.money(last)));
  }

  purchase(event) {
    event.preventDefault();
    this.refresh();
    if (this.button.disabled || !this.dialog?.open || !this.consent?.checked || this.selectionKey() !== this.dialogSelection) return;
    const form = this.form();
    if (!form?.reportValidity()) return;
    const original = this.selectedOriginal();
    const quarter = original && this.installmentFor(original);
    const quantity = this.quantity(original);
    if (!original?.available || !quarter?.available || !this.validPlan(original, quarter) || !this.validQuantity(quantity, original) || !this.validQuantity(quantity, quarter)) return;
    const route = window.Shopify?.routes?.root || this.data.root || '/';
    const localeRoot = route.endsWith('/') ? route : `${route}/`;
    const url = new URL(`${localeRoot}cart/${quarter.id}:${quantity}?checkout`, window.location.origin);
    if (url.origin !== window.location.origin) return;
    this.busy = true;
    this.scope.dataset.umInstallmentPending = 'true';
    this.scope.dataset.umPurchasePending = 'true';
    this.scope.umPurchaseCoordinator?.refreshControls();
    this.showStatus('');
    this.refresh();
    window.location.assign(url.href);
  }
}

if (!customElements.get('um-installment-button')) customElements.define('um-installment-button', UMInstallmentButton);